import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Card,
  CardContent,
  Snackbar,
  CircularProgress,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Movie,
  AddCircle,
  Dashboard,
  Slideshow,
  ExitToApp,
  Person,
  Analytics,
  LocalOffer,
  Settings,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../api/axios';
import { handleApiError, handleError } from '../utils/errorHandler';
import MovieForm from '../components/admin/MovieForm';
import MovieList from '../components/admin/MovieList';
import MovieAnalytics from '../components/admin/MovieAnalytics';
import PromotionManager from '../components/PromotionManager';
import StatisticsPanel from '../components/StatisticsPanel';

const drawerWidth = 240;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [currentSection, setCurrentSection] = useState('dashboard');
  const [anchorEl, setAnchorEl] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [movieRequests, setMovieRequests] = useState([]);
  const [showRequests, setShowRequests] = useState(false);

  // Fetch dashboard stats with auto-refresh
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['adminStats'],
    queryFn: async () => {
      const response = await api.get('/api/admin/statistics');
      return response.data;
    },
    refetchInterval: 30000, // Refresh every 30 seconds
    refetchOnWindowFocus: true, // Refresh when tab becomes active
  });

  useEffect(() => {
    if (showRequests) {
      fetchMovieRequests();
    }
  }, [showRequests]);

  const fetchMovieRequests = async () => {
    try {
      const response = await api.get('/api/movie-requests');
      if (response.data.success) {
        setMovieRequests(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching movie requests:', error);
    }
  };

  const handleStatusChange = async (requestId, newStatus) => {
    try {
      const response = await api.patch(`/api/movie-requests/${requestId}`, {
        status: newStatus
      });
      if (response.data.success) {
        setMovieRequests(prev => 
          prev.map(req => 
            req._id === requestId ? { ...req, status: newStatus } : req
          )
        );
      }
    } catch (error) {
      console.error('Error updating request status:', error);
    }
  };

  const handleDeleteRequest = async (requestId) => {
    try {
      const response = await api.delete(`/api/movie-requests/${requestId}`);
      if (response.data.success) {
        setMovieRequests(prev => prev.filter(req => req._id !== requestId));
        setSnackbar({
          open: true,
          message: 'Request deleted successfully',
          severity: 'success'
        });
      }
    } catch (error) {
      console.error('Error deleting request:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete request',
        severity: 'error'
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      default: return 'warning';
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  const handleMovieSubmit = async (movieData) => {
    try {
      const response = await api.post('/api/admin/movies', movieData);
      
      if (response.data?.errors) {
        // If we have validation errors
        const errorMessages = Object.entries(response.data.errors)
          .map(([field, error]) => `${field}: ${error}`)
          .join('\n');
        
        setSnackbar({
          open: true,
          message: `Validation errors:\n${errorMessages}`,
          severity: 'error'
        });
        return;
      }
      
      // Success case
      setSnackbar({
        open: true,
        message: 'Movie added successfully!',
        severity: 'success'
      });
      setCurrentSection('movies'); // Navigate to movies list on success
      
    } catch (error) {
      handleApiError(error);
    }
  };

  const drawer = (
    <Box>
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="h6" component="div">
          MovieHub Admin
        </Typography>
      </Box>
      <List>
        <ListItem 
          component="div"
          selected={currentSection === 'dashboard'}
          onClick={() => setCurrentSection('dashboard')}
          sx={{ cursor: 'pointer' }}
        >
          <ListItemIcon>
            <Dashboard />
          </ListItemIcon>
          <ListItemText primary="Dashboard" />
        </ListItem>
        <ListItem 
          component="div"
          selected={currentSection === 'movies'}
          onClick={() => setCurrentSection('movies')}
          sx={{ cursor: 'pointer' }}
        >
          <ListItemIcon>
            <Movie />
          </ListItemIcon>
          <ListItemText primary="Manage Movies" />
        </ListItem>
        <ListItem 
          component="div"
          selected={currentSection === 'addMovie'}
          onClick={() => setCurrentSection('addMovie')}
          sx={{ cursor: 'pointer' }}
        >
          <ListItemIcon>
            <AddCircle />
          </ListItemIcon>
          <ListItemText primary="Add Movie" />
        </ListItem>
        <ListItem 
          component="div"
          selected={currentSection === 'analytics'}
          onClick={() => setCurrentSection('analytics')}
          sx={{ cursor: 'pointer' }}
        >
          <ListItemIcon>
            <Analytics />
          </ListItemIcon>
          <ListItemText primary="Movie Analytics" />
        </ListItem>
        <ListItem 
          component="div"
          selected={currentSection === 'ads'}
          onClick={() => setCurrentSection('ads')}
          sx={{ cursor: 'pointer' }}
        >
          <ListItemIcon>
            <Slideshow />
          </ListItemIcon>
          <ListItemText primary="Ads & Promotions" />
        </ListItem>
        <ListItem 
          component="div"
          selected={currentSection === 'settings'}
          onClick={() => setCurrentSection('settings')}
          sx={{ cursor: 'pointer' }}
        >
          <ListItemIcon>
            <Settings />
          </ListItemIcon>
          <ListItemText primary="Settings" />
        </ListItem>
        <ListItem 
          component="div"
          selected={currentSection === 'requests'}
          onClick={() => setCurrentSection('requests')}
          sx={{ cursor: 'pointer' }}
        >
          <ListItemIcon>
            <LocalOffer />
          </ListItemIcon>
          <ListItemText primary="Movie Requests" />
        </ListItem>
      </List>
    </Box>
  );

  const renderDashboardContent = () => {
    if (statsLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Grid container spacing={3}>
        {/* Total Movies Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Movies
              </Typography>
              <Typography variant="h4">
                {stats?.totalMovies || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Total Views Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Views
              </Typography>
              <Typography variant="h4">
                {stats?.totalViews || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Total Downloads Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Downloads
              </Typography>
              <Typography variant="h4">
                {stats?.totalDownloads || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Average Rating Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Average Rating
              </Typography>
              <Typography variant="h4">
                {(stats?.avgRating || 0).toFixed(1)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Activity
              </Typography>
              <Box>
                {stats?.recentActivity?.map((activity, index) => (
                  <Box key={index} sx={{ mb: 1 }}>
                    <Typography variant="body2" color="textSecondary">
                      {new Date(activity.date).toLocaleString()} - {activity.action}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderRequestsContent = () => {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Button
            variant="contained"
            onClick={() => setShowRequests(!showRequests)}
            sx={{ mb: 2 }}
          >
            {showRequests ? 'Hide Requests' : 'Show Requests'}
          </Button>

          {showRequests && (
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" gutterBottom component="div">
                Movie Requests
              </Typography>
              <TableContainer component={Paper} sx={{ mt: 3 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Request</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {movieRequests.map((request) => (
                      <TableRow key={request._id}>
                        <TableCell>{request.name}</TableCell>
                        <TableCell>{request.movieRequest}</TableCell>
                        <TableCell>
                          <Chip 
                            label={request.status}
                            color={getStatusColor(request.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {new Date(request.createdAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <FormControl size="small" sx={{ mr: 1 }}>
                            <Select
                              value={request.status}
                              onChange={(e) => handleStatusChange(request._id, e.target.value)}
                              sx={{ minWidth: 120 }}
                            >
                              <MenuItem value="pending">Pending</MenuItem>
                              <MenuItem value="approved">Approved</MenuItem>
                              <MenuItem value="rejected">Rejected</MenuItem>
                            </Select>
                          </FormControl>
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={() => handleDeleteRequest(request._id)}
                          >
                            Delete
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Grid>
      </Grid>
    );
  };

  const renderContent = () => {
    switch (currentSection) {
      case 'dashboard':
        return renderDashboardContent();
      case 'movies':
        return <MovieList />;
      case 'addMovie':
        return <MovieForm mode="add" onSubmit={handleMovieSubmit} />;
      case 'analytics':
        return <MovieAnalytics />;
      case 'ads':
        return <PromotionManager />;
      case 'settings':
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
              Settings
            </Typography>
            <Typography>Coming soon...</Typography>
          </Box>
        );
      case 'requests':
        return renderRequestsContent();
      default:
        return null;
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {currentSection.charAt(0).toUpperCase() + currentSection.slice(1)}
          </Typography>
          <IconButton color="inherit" onClick={handleMenuClick}>
            <Avatar>
              <Person />
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <ExitToApp fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: 8,
        }}
      >
        {renderContent()}
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </Box>
  );
};

export default AdminDashboard;
